import React, { useMemo, useState } from 'react';
import styles from './Sidebar.module.scss';
import {
    FiTag, FiRepeat, FiBarChart2, FiLayers, FiLogOut,
    FiChevronLeft, FiChevronRight, FiSend, FiSettings, FiShoppingCart, FiPackage, FiClock,
    FiHelpCircle, FiMessageSquare, FiRotateCcw
} from 'react-icons/fi';
import { FaHourglassHalf } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import LanguageSelector from '../components/LanguageSelector';
import { NavLink } from "react-router-dom";
import { useNavigate } from 'react-router-dom';
import { userCanAccessRoute } from '../utils/permissionCatalog';
import { ROUTE_PERMISSIONS, SIDEBAR_ROUTE_ORDER } from '../utils/routePermissions';

const ROUTE_ICONS = {
    stock: <FiTag />,
    movements: <FiRepeat />,
    processTransfers: <FaHourglassHalf />,
    shipments: <FiSend />,
    capacity: <FiLayers />,
    arrivals: <FiPackage />,
    orders: <FiShoppingCart />,
    pullback: <FiRotateCcw />,
    salesAnalysis: <FiBarChart2 />,
    settings: <FiSettings />,
    timeToSell: <FiClock />,
    //chat: <FiMessageSquare />,
    support: <FiHelpCircle />,
};

export default function Sidebar({ user, logout }) {
    const [collapsed, setCollapsed] = useState(false);
    const { t } = useTranslation();
    const navigate = useNavigate();

    const handleLogout = () => {
      logout();
      navigate('/', { replace: true });
    };

    const menuItems = useMemo(
        () =>
            SIDEBAR_ROUTE_ORDER.filter((id) =>
                userCanAccessRoute(user, ROUTE_PERMISSIONS[id])
            ).map((id) => ({ id, icon: ROUTE_ICONS[id] })),
        [user]
    );

    const roleCode = user?.role?.[0];
    const roleName = roleCode ? t(`roles.${roleCode}`, { defaultValue: t('no_role') }) : t('no_role');

    return (
        <aside className={styles.sidebar} style={{ width: collapsed ? 80 : 240 }}>
            <div className={styles.logo}>
                {!collapsed ? (
                    <>
                        <img src="/images/main_logo_horizontal_cinza.svg" alt="Logo" />
                        <button onClick={() => setCollapsed(true)} aria-label="Collapse sidebar">
                            <FiChevronLeft />
                        </button>
                    </>
                ) : (
                    <>
                        <img
                            src="/images/icon.svg"
                            alt="Logo"
                            style={{ width: 40, height: 40, borderRadius: '50%' }}
                        />
                        <button onClick={() => setCollapsed(false)} aria-label="Expand sidebar">
                            <FiChevronRight />
                        </button>
                    </>
                )}
            </div>

            <nav className={styles.nav} style={{ marginTop: collapsed ? 8 : 0 }}>
                <ul>
                    {menuItems.map(({ id, icon }) => (
                        <NavLink
                            to={"/" + id}
                            key={id}
                            className={({ isActive }) => `${isActive ? styles.active : ''}`}
                            title={collapsed ? t("menu." + id) : undefined}
                        >
                            {icon}
                            {!collapsed && <span>{t("menu." + id)}</span>}
                        </NavLink>
                    ))}
                </ul>
            </nav>

            {!collapsed && <LanguageSelector />}

            <div className={styles.footer}>
                <div className={styles.user}>
                    <img
                        src={`/images/avatar.png`}
                        alt="User"
                    />
                    {!collapsed && (
                        <div className={styles.userInfo}>
                            <span className={styles.username}>
                                {user?.username || `${user?.given_name || ''} ${user?.family_name || ''}`.trim() || user?.email || 'User'}
                            </span>
                            <span className={styles.role}>{roleName}</span>
                        </div>
                    )}
                </div>
                <button className={styles.logout} onClick={handleLogout}>
                    <FiLogOut /> {!collapsed && <span>{t('logout') || 'Logout'}</span>}
                </button>
            </div>
        </aside>
    );
}
